import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { QcIssue, QC_ISSUE_CATEGORY_LABELS } from '../../../interfaces/qcIssue.interface';
import { QC_CATEGORY_RGB, ACCENT_BLUE, ERROR_RED, FIRST_PASS_PURPLE } from './qcTheme';
import {
  CategoryChip,
  CloseButton,
  FirstPassButton,
  LinkButton,
  ModalHeader,
  ModalTitle,
  SectionCaption,
} from './workspaceModalParts';

// Both dialogs share the prototype's training-feedback anatomy: draft preview, star rating, private note, purple lock notice.

const PurpleLede = styled('p')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.78rem',
  color: FIRST_PASS_PURPLE,
  margin: '5px 0 0',
  '& .MuiSvgIcon-root': { fontSize: '15px' },
});

const Body = styled('div')({
  padding: '20px 24px 4px',
});

const PreviewBox = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '9px',
  background: '#fafbfe',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '4px',
  padding: '12px 14px',
  marginBottom: '18px',
});

const PreviewText = styled('span')({
  fontSize: '0.85rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.78)',
});

const CaptionRow = styled('div')({
  margin: '0 0 10px',
});

const OptionalTag = styled('span')({
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 0,
  color: 'rgba(0,0,0,0.5)',
});

const StarRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

const StarButton = styled('button', {
  shouldForwardProp: prop => prop !== 'filled',
})<{ filled: boolean }>(({ filled }) => ({
  border: 'none',
  background: 'none',
  padding: 0,
  display: 'inline-flex',
  cursor: 'pointer',
  color: filled ? FIRST_PASS_PURPLE : 'rgba(0,0,0,0.3)',
  '& .MuiSvgIcon-root': { fontSize: '30px' },
}));

const StarCaption = styled('span')({
  fontSize: '0.8rem',
  color: 'rgba(0,0,0,0.55)',
  marginLeft: '8px',
});

const FeedbackTextarea = styled('textarea', {
  shouldForwardProp: prop => prop !== 'minHeight',
})<{ minHeight: number }>(({ minHeight }) => ({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: `${minHeight}px`,
  border: '1px solid rgba(0,0,0,0.42)',
  borderRadius: '4px',
  padding: '10px 12px',
  fontFamily: 'inherit',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
  },
}));

const LockNote = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '9px',
  background: 'rgba(94,53,177,0.07)',
  border: '1px solid rgba(94,53,177,0.25)',
  borderRadius: '4px',
  padding: '10px 12px',
  marginTop: '14px',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    color: FIRST_PASS_PURPLE,
    marginTop: '1px',
    flex: 'none',
  },
});

const LockText = styled('span')({
  fontSize: '0.8rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.72)',
});

const Footnote = styled('p')({
  fontSize: '0.78rem',
  color: 'rgba(0,0,0,0.55)',
  margin: '12px 0 0',
});

const ReasonRow = styled('div')({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
});

const ReasonChip = styled('button', {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: '30px',
  padding: '0 13px',
  borderRadius: '15px',
  fontSize: '0.78rem',
  fontWeight: selected ? 600 : 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  background: selected ? 'rgba(198,40,40,0.12)' : '#fff',
  color: selected ? ERROR_RED : 'rgba(0,0,0,0.7)',
  border: selected ? '1px solid rgba(198,40,40,0.55)' : '1px solid rgba(0,0,0,0.2)',
}));

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '20px 24px 22px',
});

const RejectCommitButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  background: ERROR_RED,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '9px 20px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: '#b02525' },
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.38)',
    cursor: 'not-allowed',
  },
});

const STAR_LABELS = ['', 'Off base', 'Needed work', 'Usable', 'Good draft', 'Exactly right'];

export const FIRST_PASS_REJECT_REASONS = [
  'Not a real issue',
  'Wrong control referenced',
  'Duplicate of another issue',
  'Evidence was sufficient',
  'Wrong issue type',
  'Other',
];

function DraftRating({ rating, onRate }: { rating: number; onRate: (value: number) => void }): JSX.Element {
  const [hover, setHover] = useState(0);
  const shown = hover || rating;
  return (
    <StarRow onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(value => (
        <StarButton
          key={value}
          type='button'
          filled={value <= shown}
          aria-label={`${value} star${value === 1 ? '' : 's'}`}
          onClick={() => onRate(value)}
          onMouseEnter={() => setHover(value)}
        >
          {value <= shown ? <StarIcon /> : <StarBorderIcon />}
        </StarButton>
      ))}
      <StarCaption>{STAR_LABELS[shown]}</StarCaption>
    </StarRow>
  );
}

function DraftPreview({ issue }: { issue: QcIssue }): JSX.Element {
  return (
    <PreviewBox>
      <CategoryChip rgb={QC_CATEGORY_RGB[issue.category]}>
        {QC_ISSUE_CATEGORY_LABELS[issue.category]}
      </CategoryChip>
      <PreviewText>{issue.text}</PreviewText>
    </PreviewBox>
  );
}

export interface FirstPassFeedback {
  rating: number;
  comment: string;
}

export interface FirstPassRejection extends FirstPassFeedback {
  reason: string;
}

export interface AcceptFirstPassIssueModalProps {
  open: boolean;
  issue: QcIssue;
  onAccept: (feedback: FirstPassFeedback) => void;
  onClose: () => void;
}

export function AcceptFirstPassIssueModal({ open, issue, onAccept, onClose }: AcceptFirstPassIssueModalProps): JSX.Element {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const reset = () => {
    setRating(0);
    setComment('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-label='Accept First Pass issue'
      PaperProps={{ sx: { width: 760, maxWidth: '100%', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <ModalHeader>
        <div>
          <ModalTitle>Accept First Pass Issue</ModalTitle>
          <PurpleLede>
            <AutoAwesomeIcon />
            Your feedback trains First Pass to draft better issues.
          </PurpleLede>
        </div>
        <CloseButton type='button' aria-label='Close accept First Pass issue' onClick={handleClose}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </CloseButton>
      </ModalHeader>
      <Body>
        <DraftPreview issue={issue} />
        <CaptionRow>
          <SectionCaption>How good was this draft?</SectionCaption>
        </CaptionRow>
        <DraftRating rating={rating} onRate={setRating} />
        <CaptionRow sx={{ margin: '20px 0 8px' }}>
          <SectionCaption>
            Comment <OptionalTag>(optional)</OptionalTag>
          </SectionCaption>
        </CaptionRow>
        <FeedbackTextarea
          minHeight={96}
          aria-label='Comment'
          placeholder='Private feedback for First Pass training — never shown to the assessor.'
          value={comment}
          onChange={event => setComment(event.target.value)}
        />
        <LockNote>
          <LockIcon />
          <LockText>
            The rating and comment are used <strong>only to train First Pass</strong>. They are never
            shown to the assessor or the service provider, and they don&apos;t appear on the QC issue.
          </LockText>
        </LockNote>
        <Footnote>
          Accepting posts the issue to the assessor as Pending Assessor Action, attributed to you
          with an &ldquo;Accepted from First Pass&rdquo; line.
        </Footnote>
      </Body>
      <Footer>
        <LinkButton type='button' onClick={handleClose}>Cancel</LinkButton>
        <FirstPassButton type='button' onClick={() => { onAccept({ rating, comment: comment.trim() }); reset(); }}>
          Accept Issue
        </FirstPassButton>
      </Footer>
    </Dialog>
  );
}

export interface RejectFirstPassIssueModalProps {
  open: boolean;
  issue: QcIssue;
  onReject: (rejection: FirstPassRejection) => void;
  onClose: () => void;
}

export function RejectFirstPassIssueModal({ open, issue, onReject, onClose }: RejectFirstPassIssueModalProps): JSX.Element {
  const [rating, setRating] = useState(0);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');

  const reset = () => {
    setRating(0);
    setReason('');
    setComment('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-label='Reject First Pass issue'
      PaperProps={{ sx: { width: 760, maxWidth: '100%', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <ModalHeader>
        <div>
          <ModalTitle>Reject First Pass Issue</ModalTitle>
          <PurpleLede>
            <AutoAwesomeIcon />
            Your reason trains First Pass to draft better issues.
          </PurpleLede>
        </div>
        <CloseButton type='button' aria-label='Close reject First Pass issue' onClick={handleClose}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </CloseButton>
      </ModalHeader>
      <Body>
        <DraftPreview issue={issue} />
        <CaptionRow>
          <SectionCaption>How good was this draft?</SectionCaption>
        </CaptionRow>
        <DraftRating rating={rating} onRate={setRating} />
        <CaptionRow sx={{ margin: '20px 0 10px' }}>
          <SectionCaption>Why are you rejecting this?</SectionCaption>
        </CaptionRow>
        <ReasonRow>
          {FIRST_PASS_REJECT_REASONS.map(option => (
            <ReasonChip
              key={option}
              type='button'
              selected={reason === option}
              onClick={() => setReason(option)}
            >
              {option}
            </ReasonChip>
          ))}
        </ReasonRow>
        <CaptionRow sx={{ margin: '20px 0 8px' }}>
          <SectionCaption>
            What should it have caught instead? <OptionalTag>(optional)</OptionalTag>
          </SectionCaption>
        </CaptionRow>
        <FeedbackTextarea
          minHeight={110}
          aria-label='What should it have caught instead?'
          placeholder='Private feedback for First Pass training — never shown to the assessor.'
          value={comment}
          onChange={event => setComment(event.target.value)}
        />
        <LockNote>
          <LockIcon />
          <LockText>
            The reason and comment are used <strong>only to train First Pass</strong>. They are never
            shown to the assessor or the service provider, and the rejected draft leaves no trace on
            the assessment.
          </LockText>
        </LockNote>
      </Body>
      <Footer>
        <LinkButton type='button' onClick={handleClose}>Cancel</LinkButton>
        <RejectCommitButton
          type='button'
          disabled={!reason}
          onClick={() => { onReject({ rating, reason, comment: comment.trim() }); reset(); }}
        >
          Reject Issue
        </RejectCommitButton>
      </Footer>
    </Dialog>
  );
}
